<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import PlayerDetailsSidebar from '../PlayerDetailsSidebar.vue'
import PlayerStatsSummary from '../PlayerStatsSummary.vue'
import PlayerGameStatsTable from '../PlayerGameStatsTable.vue'
import axios from '../../axios'

// Props definition
const props = defineProps({
  league: {
    type: Object,
    required: true
  },
  team: {
    type: Object,
    required: true
  },
  playerId: {
    type: [String, Number],
    required: true
  },
  timeFrame: {
    type: String,
    default: null
  },
  show: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['close'])

// Simple reactive state
const loading = ref(false)
const rows = ref([])
const filteredGames = ref([])
const calculatedStats = ref(null)

// Settings state
const settings = ref({
  seasons: [],
  leagues: [],
  additionalFilter: 'allGames'
})

// Available data
const seasons = [
  '2025-2026', '2024-2025', '2023-2024', '2022-2023', '2021-2022', 
  '2020-2021', '2019-2020', '2018-2019', '2017-2018'
]

const availableLeagues = computed(() => [
  { id: 'friendly-international', name: 'Friendly International' },
  { id: 'adriatic-aba-liga-2', name: 'Adriatic ABA Liga 2' },
  { id: 'adriatic-liga-aba', name: 'Adriatic Liga ABA' },
  { id: 'adriatic-liga-aba-playoffs', name: 'Adriatic Liga ABA Playoffs' },
  { id: 'argentina-la-liga-torneo-super-20', name: 'Argentina La Liga Torneo Super 20' },
  { id: 'argentina-liga-a', name: 'Argentina Liga A' },
  { id: 'australia-nbl', name: 'Australia NBL' },
  { id: 'austria-superliga', name: 'Austria Superliga' },
  { id: 'basketball-champions-league', name: 'Basketball Champions League' },
  { id: 'basketball-champions-league-americas', name: 'Basketball Champions League Americas' },
  { id: 'bnxt-league', name: 'BNXT League' },
  { id: 'bosnia-division-i', name: 'Bosnia Division I' },
  { id: 'bulgaria-nbl', name: 'Bulgaria NBL' },
  { id: 'china-cba', name: 'China CBA' },
  { id: 'croatia-liga', name: 'Croatia Liga' },
  { id: 'czech-republic-mattoni-nbl', name: 'Czech Republic Mattoni NBL' },
  { id: 'denmark-ligaen', name: 'Denmark Ligaen' },
  { id: 'eurocup', name: 'EuroCup' },
  { id: 'euroleague', name: 'EuroLeague' },
  { id: 'fiba-europe-cup', name: 'FIBA Europe Cup' },
  { id: 'finland-korisliiga', name: 'Finland Korisliiga' },
  { id: 'france-betclic-elite', name: 'France Betclic Elite' },
  { id: 'france-betclic-elite-playoffs', name: 'France Betclic Elite Playoffs' },
  { id: 'france-elite-2', name: 'France Elite 2' },
  { id: 'georgia-superleague', name: 'Georgia Superleague' },
  { id: 'germany-easycredit-bbl', name: 'Germany EasyCredit BBL' },
  { id: 'germany-pro-a', name: 'Germany Pro A' },
  { id: 'greece-heba-a1', name: 'Greece HEBA A1' },
  { id: 'greece-heba-a2', name: 'Greece HEBA A2' },
  { id: 'hungary-a-league', name: 'Hungary A League' },
  { id: 'iceland-express-league', name: 'Iceland Express League' },
  { id: 'israel-winner-league', name: 'Israel Winner League' },
  { id: 'italy-lba-serie-a', name: 'Italy LBA Serie A' },
  { id: 'italy-serie-a2', name: 'Italy Serie A2' },
  { id: 'japan-b1-league', name: 'Japan B1 League' },
  { id: 'japan-b2-league', name: 'Japan B2 League' },
  { id: 'latvian-estonian-basketball-league', name: 'Latvian-Estonian Basketball League' },
  { id: 'lithuania-betsafe-lkl', name: 'Lithuania Betsafe LKL' },
  { id: 'lithuania-betsafe-lkl-playoffs', name: 'Lithuania Betsafe LKL Playoffs' },
  { id: 'lithuania-nkl', name: 'Lithuania NKL' },
  { id: 'mexico-liga-sisnova-lnbp', name: 'Mexico Liga Sisnova LNBP' },
  { id: 'nba', name: 'NBA' },
  { id: 'ncaa', name: 'NCAA' },
  { id: 'new-zealand-nbl', name: 'New Zealand NBL' },
  { id: 'norway-blno', name: 'Norway BLNO' },
  { id: 'poland-plk', name: 'Poland PLK' },
  { id: 'portugal-liga-profissional', name: 'Portugal Liga Profissional' },
  { id: 'puerto-rico-bsn', name: 'Puerto Rico BSN' },
  { id: 'romania-division-a', name: 'Romania Division A' },
  { id: 'serbia-kls', name: 'Serbia KLS' },
  { id: 'slovakia-extraliga', name: 'Slovakia Extraliga' },
  { id: 'slovenia-liga-nova-kbm', name: 'Slovenia Liga Nova KBM' },
  { id: 'spain-leb-gold', name: 'Spain LEB Gold' },
  { id: 'spain-liga-endesa', name: 'Spain Liga Endesa' },
  { id: 'super-league-basketball', name: 'Super League Basketball' },
  { id: 'sweden-basketligan', name: 'Sweden Basketligan' },
  { id: 'switzerland-sbl', name: 'Switzerland SBL' },
  { id: 'turkey-bsl', name: 'Turkey BSL' },
  { id: 'turkey-tbl', name: 'Turkey TBL' },
  { id: 'vtb-united-league', name: 'VTB United League' },
  { id: 'world-cup', name: 'World Cup' },
  { id: 'eurobasket', name: 'EuroBasket' },
  { id: 'eurobasket-2025', name: 'EuroBasket 2025' },
  { id: 'world-cup-european-qualifiers', name: 'World Cup European Qualifiers' },
  { id: 'eurobasket-qualifying-rounds', name: 'EuroBasket Qualifying Rounds' },
  { id: 'olympic-games', name: 'Olympic Games' },
  { id: 'olympic-games-qualifying-tournament', name: 'Olympic Games Qualifying Tournament' },
  { id: 'basketball-champions-league-qualifying-rounds', name: 'Basketball Champions League Qualifying Rounds' },
  { id: 'preparation', name: 'Preparation' },
  { id: 'enbl', name: 'ENBL' },
  { id: 'brazil-nbb', name: 'Brazil NBB' },
  { id: 'brazilian-nbb', name: 'Brazilian NBB' },
  { id: 'argentina-liga-a', name: 'Argentina Liga A' },
  { id: 'king-mindaugas-cup-play-off', name: 'King Mindaugas Cup Play Off' },
  { id: 'latvian-cup', name: 'Latvian Cup' },
  { id: 'turkish-cup', name: 'Turkish Cup' },
  { id: 'italian-cup', name: 'Italian Cup' },
  { id: 'spanish-cup', name: 'Spanish Cup' },
  { id: 'world-cup-european-pre-qualifiers', name: 'World Cup European Pre Qualifiers' },
])

const columns = [
  { key: 'game_date', label: 'Date' },
  { key: 'season', label: 'Season' },
  { key: 'league_name', label: 'League' },
  { key: 'team_name', label: 'Team' },
  { key: 'opponent_team_name', label: 'Opponent' },
  { key: 'minutes', label: 'Min' },
  { key: 'points', label: 'PTS' },
  { key: 'total_rebounds', label: 'REB' },
  { key: 'assists', label: 'AST' },
  { key: 'three_point_field_goal', label: '3PM' },
  { key: 'steals', label: 'STL' },
  { key: 'blocks', label: 'BLK' },
  { key: 'turnovers', label: 'TO' },
  { key: 'pra', label: 'PRA' },
  { key: 'efficiency', label: 'EFF' }
]

// Computed properties
const currentLeagueId = computed(() => props.league?.league_id?.toString() || props.league?.league?.id?.toString())
const currentLeagueName = computed(() => props.league?.league?.league_name || 'Unknown League')
const hasExistingSettings = computed(() => !!getExistingSettings())

// Simple functions
function getExistingSettings() {
  if (!props.league?.league?.projections_settings_player_minutes_settings) return null
  return props.league.league.projections_settings_player_minutes_settings.find(
    setting => setting.template === props.timeFrame
  )
}

function getDefaultSeasons() {
  if (!props.timeFrame) return ['2024-2025']
  const seasonMap = {
    'lastFiveGames': ['2024-2025'],
    'restOfSeason': ['2024-2025'],
    'lastSeason': ['2023-2024'],
    'twoSeasonsAgo': ['2022-2023'],
    'threeSeasonsAgo': ['2021-2022']
  }
  return seasonMap[props.timeFrame] || ['2024-2025']
}

// Initialize settings
function initializeSettings() {
  const existingSettings = getExistingSettings()
  
  if (existingSettings) {
    // Use saved settings
    settings.value.seasons = existingSettings.seasons || ['2024-2025']
    settings.value.leagues = existingSettings.leagues || []
    settings.value.additionalFilter = existingSettings.additional_filter || 'allGames'
    
    // Set calculated stats
    if (existingSettings.calculated_stats) {
      calculatedStats.value = existingSettings.calculated_stats
    }
    
    console.log('Applied existing settings:', settings.value)
    console.log('Additional filter from settings:', existingSettings.additional_filter)
    console.log('Applied additional filter:', settings.value.additionalFilter)
  } else {
    // Use defaults
    settings.value.seasons = getDefaultSeasons()
    settings.value.leagues = [currentLeagueId.value]
    settings.value.additionalFilter = 'allGames'
    console.log('Applied default settings:', settings.value)
  }
}

// Fetch games
async function fetchGames(leagues, seasons, additionalFilter) {
  loading.value = true
  rows.value = []
  filteredGames.value = []
  
  try {
    const requestBody = {
      player_id: props.playerId,
      seasons: seasons.length > 0 ? seasons : [],
      leagues: leagues.length > 0 ? leagues : [currentLeagueId.value],
      additional_filter: additionalFilter
    }

    console.log('Fetching games with:', requestBody)
    
    const response = await axios.post('/api/v2/games/player/filters', requestBody)
    let data = response.data || []
    
    // Apply additional filtering
    data = applyAdditionalFilter(data, additionalFilter)
    
    rows.value = [...data]
    filteredGames.value = [...data]
    
  } catch (error) {
    console.error('Error fetching games:', error)
    rows.value = []
    filteredGames.value = []
  } finally {
    loading.value = false
  }
}

function applyAdditionalFilter(games, filterType) {
  if (!games || games.length === 0) return games
  
  switch (filterType) {
    case 'lastFiveGames':
      const sortedGames = [...games].sort((a, b) => new Date(b.game_date) - new Date(a.game_date))
      return sortedGames.slice(0, 5)
      
    case 'restOfSeason':
      const sortedGamesForRest = [...games].sort((a, b) => new Date(b.game_date) - new Date(a.game_date))
      return sortedGamesForRest.slice(5)
      
    case 'allGames':
    default:
      return games
  }
}

// Create mock games from saved stats
function createMockGamesFromSavedStats(existingSettings) {
  const mockGames = []
  const gameCount = existingSettings.calculated_stats.gameCount || 0
  const stats = existingSettings.calculated_stats.stats
  
  for (let i = 0; i < Math.min(gameCount, 10); i++) {
    mockGames.push({
      id: i + 1,
      game_date: new Date().toISOString().split('T')[0],
      season: existingSettings.seasons?.[0] || '2024-2025',
      league_name: currentLeagueName.value || 'Unknown League',
      team_name: props.team.team_name || 'Unknown Team',
      opponent_team_name: 'Opponent',
      minutes: parseFloat(existingSettings.per_game_minutes) || 0,
      points: (stats.points?.total / gameCount) || 0,
      rebounds: (stats.rebounds?.total / gameCount) || 0,
      assists: (stats.assists?.total / gameCount) || 0,
      three_point_field_goal: (stats['3pt']?.total / gameCount) || 0,
      steals: (stats.steals?.total / gameCount) || 0,
      blocks: (stats.blocks?.total / gameCount) || 0,
      turnovers: (stats.turnovers?.total / gameCount) || 0,
      pra: (stats.pra?.total / gameCount) || 0,
      efficiency: 0
    })
  }
  
  return mockGames
}

// Save template
async function saveLeagueTemplate() {
  if (!calculatedStats.value) {
    console.log('No stats calculated yet')
    return
  }
  
  const totalMinutes = filteredGames.value.reduce((total, game) => {
    return total + (parseFloat(game.minutes) || 0)
  }, 0)
  
  const gameCount = filteredGames.value.length
  const perGameMinutes = gameCount > 0 ? totalMinutes / gameCount : 0
  
  const templateType = props.timeFrame || 'league'
  const timeFrameLabel = props.timeFrame ? getTimeFrameLabel(props.timeFrame) : 'All'
  const additionalFilterLabel = getAdditionalFilterLabel(settings.value.additionalFilter)
  const templateName = `${currentLeagueName.value} - ${timeFrameLabel} - ${additionalFilterLabel}`
  
  const filterData = {
    player_id: props.playerId,
    league_id: props.league.league_id,
    team_id: props.team.id,
    time_frame: props.timeFrame,
    additional_filter: settings.value.additionalFilter,
    template: templateType,
    league_template: templateName,
    leagues: settings.value.leagues,
    seasons: settings.value.seasons,
    filteredGames: filteredGames.value.length,
    totalMinutes: totalMinutes,
    perGameMinutes: perGameMinutes,
    calculatedStats: calculatedStats.value,
    totals: createTotalsObject(),
    per30: createPer30Object()
  }
  
  try {
    const response = await axios.post('/api/v2/projections-players/minute-settings/league-templates/save', filterData)
    console.log('Template saved:', response.data)
    alert(`League template "${templateName}" saved successfully!`)
  } catch (error) {
    console.error('Error saving template:', error)
    alert('Error saving league template. Please try again.')
  }
}

function createTotalsObject() {
  const stats = calculatedStats.value.stats
  return {
    points: stats.points?.total || 0,
    rebounds: stats.rebounds?.total || 0,
    assists: stats.assists?.total || 0,
    three_pointers: stats['3pt']?.total || 0,
    pra: stats.pra?.total || 0,
    turnovers: stats.turnovers?.total || 0,
    blocks: stats.blocks?.total || 0,
    steals: stats.steals?.total || 0,
    minutes: filteredGames.value.reduce((total, game) => total + (parseFloat(game.minutes) || 0), 0)
  }
}

function createPer30Object() {
  const stats = calculatedStats.value.stats
  return {
    points: stats.points?.per30 || 0,
    rebounds: stats.rebounds?.per30 || 0,
    assists: stats.assists?.per30 || 0,
    three_pointers: stats['3pt']?.per30 || 0,
    pra: stats.pra?.per30 || 0,
    turnovers: stats.turnovers?.per30 || 0,
    blocks: stats.blocks?.per30 || 0,
    steals: stats.steals?.per30 || 0
  }
}

// Utility functions
function getTimeFrameLabel(timeFrame) {
  const labels = {
    'lastFiveGames': 'Last 5 Games',
    'restOfSeason': 'Rest of Season',
    'lastSeason': 'Last Season',
    'twoSeasonsAgo': '2 Seasons Ago',
    'threeSeasonsAgo': '3 Seasons Ago'
  }
  return labels[timeFrame] || 'All'
}

function getAdditionalFilterLabel(filterType) {
  const labels = {
    'lastFiveGames': 'Last 5 Games',
    'withoutLast5Games': 'Rest of Season (Without Last 5)',
    'allGames': 'All Games'
  }
  return labels[filterType] || 'All Games'
}

// Event handlers
function closeModal() {
  emit('close')
}

function handleStatsUpdated(filteredStats) {
  console.log('Filtered stats updated:', filteredStats)
}

function handleFilteredRowsUpdated(filteredRowsData) {
  filteredGames.value = filteredRowsData
}

function handleStatsCalculated(stats) {
  calculatedStats.value = stats
  console.log('Stats calculated:', stats)
}

// Watchers
watch(
  () => [settings.value.leagues, settings.value.seasons, settings.value.additionalFilter],
  async ([leagues, seasons, additionalFilter]) => {
    console.log('Settings changed, fetching games:', { leagues, seasons, additionalFilter })
    await fetchGames(leagues, seasons, additionalFilter)
  },
  { deep: true }
)

watch(rows, (newRows) => {
  if (newRows && newRows.length > 0) {
    filteredGames.value = [...newRows]
  } else {
    filteredGames.value = []
  }
}, { immediate: true })

// Lifecycle
onMounted(async () => {
  try {
    initializeSettings()
    const existingSettings = getExistingSettings()
    
    if (existingSettings && existingSettings.calculated_stats) {
      // Use existing data
      const mockGames = createMockGamesFromSavedStats(existingSettings)
      rows.value = [...mockGames]
      filteredGames.value = [...mockGames]
    } else {
      // Fetch new data
      await fetchGames(
        settings.value.leagues, 
        settings.value.seasons, 
        settings.value.additionalFilter
      )
    }
  } catch (error) {
    console.error('Error initializing league data:', error)
    loading.value = false
  }
})
</script>

<template>
  <!-- Overlay Container -->
  <div v-if="show" class="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-8">
    <!-- Inner Container -->
    <div class="bg-white rounded-lg shadow-2xl w-full h-full flex flex-col overflow-hidden">
      <!-- Header -->
      <div class="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 flex-shrink-0">
        <h1 class="text-2xl font-bold text-gray-900">
          League {{ league?.league_id }} Template Configuration
          <span v-if="team?.team_name" class="text-sm text-gray-500 ml-2">
            ({{ team.team_name }})
          </span>
          <span v-if="settings.seasons.length" class="text-sm text-gray-500 ml-2">
            ({{ settings.seasons.join(', ') }})
          </span>
          <span v-if="timeFrame" class="text-sm text-blue-600 ml-2 font-medium">
            [{{ timeFrame }}]
          </span>
          <span v-if="hasExistingSettings" class="text-sm text-green-600 ml-2 font-medium">
            <i class="fas fa-check-circle mr-1"></i>
            Using Saved Settings
          </span>
        </h1>
        
        <!-- Header Buttons -->
        <div class="flex items-center gap-3">
          <!-- Save League Template Button -->
          <button
            @click="saveLeagueTemplate"
            class="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 font-semibold"
          >
            <i class="fas fa-save"></i>
            Save {{ timeFrame ? getTimeFrameLabel(timeFrame) : 'League' }} Template
          </button>
          
          <!-- Close Button -->
          <button
            type="button"
            class="text-gray-400 hover:text-gray-600 focus:outline-none bg-white hover:bg-gray-50 rounded-lg p-3 transition-colors duration-200 border border-gray-200"
            @click="closeModal"
          >
            <span class="sr-only">Close</span>
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="flex flex-1 overflow-hidden">
        <!-- Sidebar -->
        <div class="w-80 bg-white border-r border-gray-200 flex-shrink-0 overflow-y-auto">
          <!-- Settings Status Info -->
          <div v-if="hasExistingSettings" class="p-4 bg-green-50 border-b border-green-200">
            <div class="flex items-center text-green-800">
              <i class="fas fa-info-circle mr-2"></i>
              <span class="text-sm font-medium">Using Saved Settings</span>
            </div>
            <div class="mt-2 text-xs text-green-700">
              <div>Seasons: {{ settings.seasons.join(', ') }}</div>
              <div>Leagues: {{ settings.leagues.join(', ') }}</div>
              <div>Additional Filter: {{ getAdditionalFilterLabel(settings.additionalFilter) }}</div>
            </div>
          </div>
          
          <PlayerDetailsSidebar
            :seasons="seasons"
            :selected-season="settings.seasons"
            :selected-leagues="settings.leagues"
            :available-leagues="availableLeagues"
            :selected-additional-filter="settings.additionalFilter"
            @update:selectedSeason="val => settings.seasons = val"
            @update:selectedLeagues="val => settings.leagues = val"
            @update:additionalFilter="val => settings.additionalFilter = val"
          />
        </div>
        
        <!-- Main Content -->
        <div class="flex-1 flex flex-col bg-gray-50 overflow-y-auto">
          <!-- Stats Summary -->
          <div class="flex-shrink-0">
            <PlayerStatsSummary 
              :games="filteredGames" 
              :projected-minutes="30" 
              @stats-calculated="handleStatsCalculated"
            />
          </div>
          
          <!-- Table -->
          <div class="flex-1 overflow-y-auto p-6">
            <PlayerGameStatsTable 
              :key="`league-table-${props.playerId}-${league?.league_id}-${settings.seasons.join('-')}-${settings.leagues.join('-')}`"
              :columns="columns" 
              :rows="rows" 
              @stats-updated="handleStatsUpdated" 
              @filtered-rows-updated="handleFilteredRowsUpdated" 
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
